import { type ReactNode } from 'react';

import { ToolChip } from '@backrs_ui/grow-ui/src/components/Nuggets/ToolChip';
import { type StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { headerTextSx, mutedTextSx } from './analytics.styles';

type SubsectionHeaderProps = Readonly<{
  title: string;
  /** Tool the section reports on; the badge shows its banner and opens it. */
  toolId: StickerCardId;
  subtitle: string;
  /** Sits beside the title, dropping below it once the row is too narrow. */
  action?: ReactNode;
}>;

/** A divider, then the title as a large badge. */
export const SubsectionHeader = ({ title, toolId, subtitle, action }: SubsectionHeaderProps) => {
  const heading = (
    <Box sx={headerTextSx}>
      <Box sx={chipRowSx}>
        <ToolChip toolId={toolId} label={title} size="xl" />
      </Box>
      <Typography variant="text-sm-regular" sx={mutedTextSx}>
        {subtitle}
      </Typography>
    </Box>
  );

  return (
    <Box sx={wrapSx}>
      <Box sx={dividerSx} />
      {action ? (
        <Box sx={headerRowSx}>
          {heading}
          <Box sx={actionSx}>{action}</Box>
        </Box>
      ) : (
        heading
      )}
    </Box>
  );
};

const chipRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
};

const wrapSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  width: '100%',
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const headerRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
});

const actionSx: SxProps<Theme> = {
  display: 'flex',
  flexShrink: 0,
};
